import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchClassesList,
  deleteClass,
  updateClass,
  createClass,
  setClassInProgressOfCancellation,
  reactivateClass,
  archiveClass,
  fetchArchivedClasses,
  unarchiveClass,
} from "../../../redux/admin/adminOperations";
import { Class, Instructor } from "../../../redux/types/types";
import Notiflix from "notiflix";
import { useIntl } from "react-intl";

type Order = "asc" | "desc";

export const useClassManagement = () => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const {
    classesList: classes,
    totalClassesList: totalClasses,
    archivedClasses,
    totalArchivedClasses,
    loading,
    error,
  } = useSelector((state: RootState) => state.admin);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<number | null>(null);
  const [classToEdit, setClassToEdit] = useState<Class | null>(null);
  const [page, setPage] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [archivedPage, setArchivedPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState<keyof Class>("id");
  const [order, setOrder] = useState<Order>("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(false);

  const fetchClassData = useCallback(() => {
    dispatch(
      fetchClassesList({
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
        search: searchTerm,
      })
    ); // eslint-disable-next-line
  }, [dispatch, page, rowsPerPage, orderBy, order, searchTerm, trigger]);

  const handleDeleteClass = useCallback((classId: number) => {
    setClassToDelete(classId);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteClass = useCallback(() => {
    if (classToDelete) {
      dispatch(deleteClass(classToDelete))
        .unwrap()
        .then(() => {
          fetchClassData();
          setDeleteDialogOpen(false);
          setClassToDelete(null);
          setDeleteError(null);
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.deleteClassSuccess" })
          );
        })
        .catch((error) => {
          let errorKey = "error.failedToDeleteClass";
          if (error.message === "Class not found.") {
            errorKey = "error.classNotFound";
          } else if (
            error.message ===
            "Cannot delete class because it is associated with other records."
          ) {
            errorKey = "error.classInUse";
          } else if (error.message === "An unexpected error occurred.") {
            errorKey = "error.unexpectedError";
          }
          setDeleteError(errorKey);
        });
    }
  }, [classToDelete, dispatch, fetchClassData, intl]);

  const handleEditClass = useCallback((classItem: Class) => {
    setClassToEdit(classItem);
    setEditDialogOpen(true);
  }, []);

  const handleUpdateClass = useCallback(
    (
      updatedClass: Omit<Class, "instructors"> & {
        instructors: Instructor[];
      }
    ) => {
      if (updatedClass && updatedClass.id) {
        const { schedules, statusHistory, ...remainingUpdatedClass } =
          updatedClass;

        dispatch(
          updateClass({
            classId: updatedClass.id,
            classData: remainingUpdatedClass,
          })
        )
          .unwrap()
          .then(() => {
            Notiflix.Notify.success(
              intl.formatMessage({ id: "admin.dashboard.updateClassSuccess" })
            );
            fetchClassData();
            setEditDialogOpen(false);
            setClassToEdit(null);
          })
          .catch((error) => {
            Notiflix.Notify.failure(
              intl.formatMessage({ id: "admin.dashboard.updateClassError" })
            );
            console.error("Failed to update class:", error);
          });
      }
    },
    // eslint-disable-next-line
    [dispatch, fetchClassData]
  );

  const handleCreateClass = useCallback(
    async (
      classData: Omit<Class, "id" | "instructors"> & {
        instructors: Instructor[];
      }
    ) => {
      await dispatch(createClass(classData))
        .unwrap()
        .then(async (data) => {
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.dashboard.createClassSuccess" })
          );
          const page =
            orderBy === "id" && order === "desc"
              ? 0
              : Math.ceil(data.total / rowsPerPage) - 1;
          await setPage(page);
          await setTrigger(!trigger);
          await setCreateDialogOpen(false);
        })
        .catch((error) => {
          Notiflix.Notify.failure(
            intl.formatMessage({ id: "admin.dashboard.createClassError" })
          );
          console.error("Failed to create class:", error);
        });
    },
    // eslint-disable-next-line
    [dispatch, fetchClassData]
  );

  const handleSetClassInProgressOfCancellation = useCallback(
    (classId: number, cancelDate: string) => {
      dispatch(
        setClassInProgressOfCancellation({
          classId,
          cancelDate,
          language: intl.locale,
        })
      )
        .unwrap()
        .then(() => {
          fetchClassData();
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.classInCancellationSuccess" })
          );
        })
        .catch((error) => {
          Notiflix.Notify.failure(
            intl.formatMessage({ id: "admin.classInCancellationFailure" })
          );
          console.error(
            "Failed to set class in progress of cancellation:",
            error
          );
        });
    },
    [dispatch, fetchClassData, intl]
  );

  const handleReactivateClass = useCallback(
    (classId: number) => {
      dispatch(reactivateClass(classId))
        .unwrap()
        .then(() => {
          fetchClassData();
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.reactivateClassSuccess" })
          );
        })
        .catch((error) => {
          Notiflix.Notify.failure(
            intl.formatMessage({ id: "admin.reactivateClassFailure" })
          );
          console.error("Failed to reactivate class:", error);
        });
    },
    [dispatch, fetchClassData, intl]
  );

  const fetchArchivedClassData = useCallback(() => {
    dispatch(
      fetchArchivedClasses({
        page: archivedPage + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
        search: searchTerm,
      })
    );
  }, [dispatch, archivedPage, rowsPerPage, orderBy, order, searchTerm]);

  const handleArchiveClass = useCallback(
    (classId: number) => {
      dispatch(archiveClass({ classId, language: intl.locale }))
        .unwrap()
        .then(() => {
          fetchClassData();
          if (tabValue === 1) {
            fetchArchivedClassData();
          }
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.archiveClassSuccess" })
          );
        })
        .catch((error) => {
          Notiflix.Notify.failure(
            intl.formatMessage({ id: "admin.archiveClassFailure" })
          );
          console.error("Failed to archive class:", error);
        });
    },
    [dispatch, fetchClassData, fetchArchivedClassData, intl, tabValue]
  );

  const handleUnarchiveClass = useCallback(
    (classId: number) => {
      dispatch(unarchiveClass({ classId, language: intl.locale }))
        .unwrap()
        .then(() => {
          // Check if this was the last class on the current page
          const isLastClassOnPage = archivedClasses.length === 1;
          const shouldGoToPreviousPage = isLastClassOnPage && archivedPage > 0;

          if (shouldGoToPreviousPage) {
            // Set page to previous page first
            const newPage = archivedPage - 1;
            setArchivedPage(newPage);
            // Fetch archived data for the new page
            dispatch(
              fetchArchivedClasses({
                page: newPage + 1,
                limit: rowsPerPage,
                sortBy: orderBy,
                sortOrder: order,
                search: searchTerm,
              })
            );
          } else {
            // If not going to previous page, fetch data normally
            fetchArchivedClassData();
          }

          fetchClassData();
          Notiflix.Notify.success(
            intl.formatMessage({ id: "admin.unarchiveClassSuccess" })
          );
        })
        .catch((error) => {
          Notiflix.Notify.failure(
            intl.formatMessage({ id: "admin.unarchiveClassFailure" })
          );
          console.error("Failed to unarchive class:", error);
        });
    },
    [
      dispatch,
      fetchClassData,
      fetchArchivedClassData,
      intl,
      archivedClasses.length,
      archivedPage,
      rowsPerPage,
      orderBy,
      order,
      searchTerm,
    ]
  );

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
      if (newValue === 1) {
        fetchArchivedClassData();
      }
    },
    [fetchArchivedClassData]
  );

  const handleArchivedChangePage = useCallback(
    (event: unknown, newPage: number) => {
      setArchivedPage(newPage);
    },
    []
  );

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const handleRequestSort = useCallback(
    (property: keyof Class) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      setPage(0);
    },
    []
  );

  return {
    classes,
    totalClasses,
    archivedClasses,
    totalArchivedClasses,
    loading,
    error,
    deleteDialogOpen,
    editDialogOpen,
    createDialogOpen,
    classToDelete,
    classToEdit,
    page,
    rowsPerPage,
    orderBy,
    order,
    searchTerm,
    deleteError,
    tabValue,
    archivedPage,
    fetchClassData,
    fetchArchivedClassData,
    handleDeleteClass,
    confirmDeleteClass,
    handleEditClass,
    handleUpdateClass,
    handleCreateClass,
    handleSetClassInProgressOfCancellation,
    handleReactivateClass,
    handleArchiveClass,
    handleUnarchiveClass,
    handleChangePage,
    handleChangeRowsPerPage,
    handleArchivedChangePage,
    handleRequestSort,
    handleSearchChange,
    handleTabChange,
    setDeleteDialogOpen,
    setEditDialogOpen,
    setCreateDialogOpen,
    setClassToEdit,
    setDeleteError,
    setPage,
  };
};
